/*
  <tabbed-products>

  Purpose:
    Used by sections/tabbed-products.liquid. Switches between the pre-rendered
    product slider panels when a tab is clicked - no page reload, no fetch.
    Only one panel is visible at a time (the others carry [hidden]).

  Behavior:
    - Click / Enter / Space on a tab: show its panel, mark the tab active
      (aria-selected), reset the newly opened slider to its first card and
      scroll the tab bar so the active tab is in view.
    - Left / Right arrow, Home, End move between tabs (roving tabindex).
    - Theme Editor: selecting a tab block opens that tab, so the products
      shown match what is being edited.

  The product sliders themselves are the theme's <gridy-slider> (global.js);
  nothing in it is modified - we only reset its scroll position and current
  slide when its tab opens.

  Dependencies:
    - assets/section-tabbed-products.css
*/
if (!customElements.get('tabbed-products')) {
  customElements.define('tabbed-products', class TabbedProducts extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tablist || !this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
      });

      // Theme Editor: selecting a tab block shows that tab
      this.addEventListener('shopify:block:select', (event) => {
        const tab = event.target.closest && event.target.closest('[role="tab"]');
        if (tab) this.activate(tab, { scroll: true });
      });
    }

    panelFor(tab) {
      return this.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls')));
    }

    activate(tab, { focus = false, scroll = true } = {}) {
      if (tab.getAttribute('aria-selected') === 'true') {
        if (scroll) this.scrollTabIntoView(tab);
        return;
      }

      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', selected ? 'true' : 'false');
        other.setAttribute('tabindex', selected ? '0' : '-1');
        const panel = this.panelFor(other);
        if (panel) panel.hidden = !selected;
      });

      this.resetSlider(this.panelFor(tab));
      if (focus) tab.focus();
      if (scroll) this.scrollTabIntoView(tab);
    }

    // A freshly opened tab always starts at its first card
    resetSlider(panel) {
      if (!panel) return;
      const slider = panel.querySelector('gridy-slider');
      if (!slider) return;
      if (slider.track) slider.track.scrollLeft = 0;
      if (slider.config) slider.config.current_slide = 0;
      if (slider.slides) slider.slides.forEach((slide) => slide.classList.remove('active'));
    }

    // Manual horizontal scroll of the tab bar only - scrollIntoView() could
    // also move the page vertically.
    scrollTabIntoView(tab) {
      const list = this.tablist;
      if (!list || list.scrollWidth <= list.clientWidth) return;
      const target = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: target, behavior: 'smooth' });
    }

    onKeydown(event, tab) {
      const index = this.tabs.indexOf(tab);
      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
      else if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      else if (event.key === 'Home') next = this.tabs[0];
      else if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      this.activate(next, { focus: true });
    }
  });
}
